import { all, call, put, takeLatest } from '@redux-saga/core/effects';
import {
  getPriceAlertsRequest,
  getPriceAlertsByProductRequest,
  getPriceAlertRequest,
  patchPriceAlertsRequest,
  patchPriceAlertRequest,
  deletePriceAlertRequest,
  postPriceAlertRequest,
  dismissPriceAlertBadgeRequest,
  dismissPriceAlertBannerRequest,
  setupMockPriceAlertsRequest,
  clearExpiredPriceAlertsRequest,
} from '../../request/priceAlertsRequests';
import {
  PRICE_ALERTS_FETCH,
  PRICE_ALERT_FETCH,
  PATCH_PRICE_ALERTS,
  PATCH_PRICE_ALERT,
  DELETE_PRICE_ALERT,
  POST_PRICE_ALERT,
  DISMISS_PRICE_ALERT_BADGE,
  DISMISS_PRICE_ALERT_BANNER,
  SETUP_MOCK_PRICE_ALERTS,
  CLEAR_EXPIRED_PRICE_ALERTS,
} from '../actions/priceAlerts/priceAlertsActionConstants';
import {
  fetchPriceAlertsSuccess,
  fetchPriceAlertsError,
  fetchPriceAlertSuccess,
  fetchPriceAlertError,
  patchPriceAlertsSuccess,
  patchPriceAlertsError,
  patchPriceAlertSuccess,
  patchPriceAlertError,
  deletePriceAlertSuccess,
  deletePriceAlertError,
  postPriceAlertSuccess,
  postPriceAlertError,
  clearExpiredPriceAlertsSuccess,
  clearExpiredPriceAlertsError,
} from '../actions/priceAlerts/priceAlertsActions';
import { rejectErrorCodeHelper } from '../../util/helpers/rejectErrorCodeHelper';

function* fetchPriceAlerts({ payload }) {
  try {
    const request = payload.productCode
      ? getPriceAlertsByProductRequest
      : getPriceAlertsRequest;
    const requestArg = payload.productCode ? payload : payload.accountUid;
    const { data } = yield call(request, requestArg);
    yield put(fetchPriceAlertsSuccess(data, payload.productCode));
    if (payload.onSuccess) {
      yield call(payload.onSuccess);
    }
  } catch (error) {
    const errorMessage = yield call(rejectErrorCodeHelper, error);
    yield put(fetchPriceAlertsError(errorMessage));
  }
}

function* fetchPriceAlert({ payload }) {
  try {
    const { data } = yield call(getPriceAlertRequest, payload);
    yield put(fetchPriceAlertSuccess(data));
  } catch (error) {
    const errorMessage = yield call(rejectErrorCodeHelper, error);
    yield put(fetchPriceAlertError(errorMessage));
  }
}

function* patchPriceAlerts({ payload }) {
  yield call(payload.setIsLoading, true);
  try {
    const { data } = yield call(patchPriceAlertsRequest, payload);
    yield put(patchPriceAlertsSuccess(data));
    if (payload.onSuccess) {
      yield call(payload.onSuccess);
    }
    yield call(payload.setError, false);
  } catch (error) {
    const errorMessage = yield call(rejectErrorCodeHelper, error);
    yield put(patchPriceAlertsError(errorMessage));
    yield call(payload.setError, errorMessage);
  } finally {
    yield call(payload.setIsLoading, false);
    yield call(payload.handleNext);
  }
}

function* patchPriceAlert({ payload }) {
  yield call(payload.setIsLoading, true);
  try {
    const { data } = yield call(patchPriceAlertRequest, payload);
    yield put(patchPriceAlertSuccess(data));
    if (payload.onSuccess) {
      yield call(payload.onSuccess, data);
    }
    yield call(payload.setError, false);
  } catch (error) {
    const errorMessage = yield call(rejectErrorCodeHelper, error);
    yield put(patchPriceAlertError(errorMessage));
    yield call(payload.setError, errorMessage);
  } finally {
    yield call(payload.setIsLoading, false);
    yield call(payload.handleNext);
  }
}

function* deletePriceAlert({ payload }) {
  yield call(payload.setIsLoading, true);
  try {
    const { data } = yield call(deletePriceAlertRequest, payload);
    yield put(deletePriceAlertSuccess(data));
    if (payload.onSuccess) {
      yield call(payload.onSuccess);
    }
    yield call(payload.setError, false);
  } catch (error) {
    const errorMessage = yield call(rejectErrorCodeHelper, error);
    yield put(deletePriceAlertError(errorMessage));
    yield call(payload.setError, errorMessage);
  } finally {
    yield call(payload.setIsLoading, false);
    yield call(payload.handleNext);
  }
}

function* postPriceAlert({ payload }) {
  yield call(payload.setIsLoading, true);
  try {
    const { data } = yield call(postPriceAlertRequest, payload);
    yield put(postPriceAlertSuccess(data));
    if (payload.onSuccess) {
      yield call(payload.onSuccess, data);
    }
    yield call(payload.setError, false);
  } catch (error) {
    const errorMessage = yield call(rejectErrorCodeHelper, error);
    yield put(postPriceAlertError(errorMessage));
    yield call(payload.setError, errorMessage);
  } finally {
    yield call(payload.setIsLoading, false);
    yield call(payload.handleNext);
  }
}

function* dismissPriceAlertBadge({ payload }) {
  try {
    yield call(dismissPriceAlertBadgeRequest, payload);
  } catch (error) {
    // fire-and-forget — badge dismissal failure is non-blocking
  }
}

function* dismissPriceAlertBanner({ payload }) {
  if (payload.setIsLoading) {
    yield call(payload.setIsLoading, true);
  }
  try {
    yield call(dismissPriceAlertBannerRequest, payload);
    if (payload.onSuccess) {
      yield call(payload.onSuccess);
    }
  } catch (error) {
    // banner stays visible on failure
  } finally {
    if (payload.setIsLoading) {
      yield call(payload.setIsLoading, false);
    }
  }
}

function* setupMockPriceAlerts({ payload }) {
  if (payload?.setIsLoading) {
    yield call(payload.setIsLoading, true);
  }
  try {
    yield call(setupMockPriceAlertsRequest, payload.accountUid);
    if (payload?.onSuccess) {
      yield call(payload.onSuccess);
    }
  } catch (error) {
    if (payload?.onError) {
      const errorMessage = yield call(rejectErrorCodeHelper, error);
      yield call(payload.onError, errorMessage);
    }
  } finally {
    if (payload?.setIsLoading) {
      yield call(payload.setIsLoading, false);
    }
  }
}

function* clearExpiredPriceAlerts({ payload }) {
  try {
    const { data } = yield call(clearExpiredPriceAlertsRequest, payload);
    yield put(clearExpiredPriceAlertsSuccess(data));
    if (payload.setError) {
      yield call(payload.setError, false);
    }
    if (payload.onSuccess) {
      yield call(payload.onSuccess);
    }
  } catch (error) {
    const errorMessage = yield call(rejectErrorCodeHelper, error);
    yield put(clearExpiredPriceAlertsError(errorMessage));
    if (payload.setError) {
      yield call(payload.setError, errorMessage);
    }
  }
}

export default function* priceAlertsSaga() {
  yield all([
    takeLatest(PRICE_ALERTS_FETCH, fetchPriceAlerts),
    takeLatest(PRICE_ALERT_FETCH, fetchPriceAlert),
    takeLatest(PATCH_PRICE_ALERTS, patchPriceAlerts),
    takeLatest(PATCH_PRICE_ALERT, patchPriceAlert),
    takeLatest(DELETE_PRICE_ALERT, deletePriceAlert),
    takeLatest(POST_PRICE_ALERT, postPriceAlert),
    takeLatest(DISMISS_PRICE_ALERT_BADGE, dismissPriceAlertBadge),
    takeLatest(DISMISS_PRICE_ALERT_BANNER, dismissPriceAlertBanner),
    takeLatest(SETUP_MOCK_PRICE_ALERTS, setupMockPriceAlerts),
    takeLatest(CLEAR_EXPIRED_PRICE_ALERTS, clearExpiredPriceAlerts),
  ]);
}
